import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import axiosInstance from '../api/axiosInstance';
import '../App.css'; // For modal styling

function AssociationReportDetailModal({ isOpen, onClose, task, onSaveReportDate, onReportChange, isReadOnly }) {
  const [taskSchedule, setTaskSchedule] = useState([]); // 공사 일정 상태 (schedule_entry에서 가져옴)
  const [remarks, setRemarks] = useState('');
  const [completionReportStatus, setCompletionReportStatus] = useState('');

  useEffect(() => {
    if (isOpen && task && task.id) {
      setRemarks(''); // Clear remarks immediately on open
      setCompletionReportStatus(task.completionReportStatus || '미완료');
      const fetchTaskData = async () => {
        try {
          // Fetch schedule entries
          const scheduleResponse = await axiosInstance.get(`/schedule-entries/task/${task.id}`);
          const sortedSchedule = scheduleResponse.data.sort((a, b) => new Date(b.scheduledDate) - new Date(a.scheduledDate));
          setTaskSchedule(sortedSchedule);

          // Fetch association report to get remarks
          const reportResponse = await axiosInstance.get(`/association-reports/by-task/${task.id}`);
          if (reportResponse.data && reportResponse.data.length > 0) {
            setRemarks(reportResponse.data[0].remarks || '');
          } else {
            setRemarks(''); // Ensure it's empty if no report data
          }

        } catch (error) {
          console.error('Error fetching task data:', error);
          setTaskSchedule([]);
          setRemarks('');
        }
      };
      fetchTaskData();
    } else if (!isOpen) {
      // When modal closes, reset all states
      setRemarks('');
      setTaskSchedule([]);
    }
  }, [isOpen, task]);

  const handleSaveCompletionReportStatus = async () => {
    try {
      const updatedTask = { ...task, completionReportStatus };
      await axiosInstance.put(`/tasks/${task.id}`, updatedTask);
      Swal.fire('저장 완료!', '완료보고현황이 성공적으로 저장되었습니다.', 'success');
      onReportChange(); // Refresh parent component
    } catch (error) {
      console.error('완료보고현황 저장 중 오류 발생:', error);
      Swal.fire({
        icon: 'error',
        title: '오류',
        text: error.response?.data || '완료보고현황 저장 중 오류가 발생했습니다.',
      });
    }
  };

  const handleSaveRemarks = async () => {
    try {
      // We need to send the complete object to the backend
      const reportResponse = await axiosInstance.get(`/association-reports/by-task/${task.id}`);
      const existingReport = reportResponse.data[0] || {};

      const associationData = {
        ...existingReport,
        taskId: task.id,
        taskName: task.name,
        remarks: remarks,
      };

      await axiosInstance.put(`/association-reports/by-task/${task.id}`, associationData);
      Swal.fire('저장 완료!', '비고가 성공적으로 저장되었습니다.', 'success');
      onReportChange(); // Refresh parent component
    } catch (error) {
      console.error('비고 저장 중 오류 발생:', error);
      Swal.fire({
        icon: 'error',
        title: '오류',
        text: error.response?.data || '비고 저장 중 오류가 발생했습니다.',
      });
    }
  };

  if (!isOpen || !task) return null;

  const handleScheduleClick = (entry) => {
    if (entry.scheduledDate === task.reportDate) {
      // If the clicked entry is the reported date, ask to cancel the report
      Swal.fire({
        title: '협회신고 취소',
        text: '정말로 협회신고를 취소하시겠습니까?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: '취소',
        cancelButtonText: '닫기',
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            await axiosInstance.delete(`/association-reports/by-task/${task.id}`);
            Swal.fire('취소 완료!', '협회신고가 성공적으로 취소되었습니다.', 'success');
            onReportChange(); // Trigger refresh in parent component
          } catch (error) {
            console.error('협회신고 취소 중 오류 발생:', error);
            Swal.fire({
              icon: 'error',
              title: '오류',
              text: error.response?.data || '협회신고 취소 중 오류가 발생했습니다.',
            });
          }
        }
      });
    } else {
      // Original logic for "현장 확인 신고"
      Swal.fire({
        title: '협회신고날짜',
        text: '협회신고 완료 날짜로 지정하시겠습니까?',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: '완료',
        cancelButtonText: '취소',
      }).then((result) => {
        if (result.isConfirmed) {
          onSaveReportDate(task.id, entry.scheduledDate, entry.assignedManagers, entry.assignedAssistants);
          onReportChange(); // Trigger refresh in parent component
        }
      });
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="modal-close-button">×</button>
        
        
        <div style={{ display: 'flex', flexDirection: 'row', gap: '20px' }}> {/* Flex container for two columns */}
          
          <div style={{ flex: 1, paddingRight: '10px', borderRight: '1px solid #eee' }}> {/* Left Column */}
            <h2 style={{ textAlign: 'center', marginBottom: '20px' }}>협회신고 관리</h2> {/* Main title, centered */}
            <br/><hr/>
            <div className="form-group">
              <p><label>공사명</label><strong>{task.name}</strong></p>
            </div>
            <br/>
            <div className="form-group">
              <p><label>공사번호(감리)</label><strong>{task.number}</strong></p>
            </div>
            <div className="form-group">
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <label style={{ marginRight: '10px', whiteSpace: 'nowrap' }}>완료보고:</label>
                <select style={{ width: isReadOnly ? '250px' : '200px' }} value={completionReportStatus} onChange={(e) => setCompletionReportStatus(e.target.value)} disabled={isReadOnly}>
                  <option value="미완료">미완료</option>
                  <option value="완료">완료</option>
                </select>
                {!isReadOnly && (
                  <button onClick={handleSaveCompletionReportStatus} className="submit-button" style={{ marginLeft: '10px', whiteSpace: 'nowrap' }}>저장</button>
                )}
              </div>
            </div>
            <div className="form-group" style={{ marginTop: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>비고</label>
              <textarea
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                rows="4"
                style={{ width: '90%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                readOnly={isReadOnly} // Make textarea read-only
              />
              {!isReadOnly && ( // Conditionally render save button
                <button onClick={handleSaveRemarks} className="submit-button" style={{ marginTop: '10px' }}>비고 저장</button>
              )}
            </div>
          </div>

          <div style={{ flex: 2, paddingLeft: '10px' }}> {/* Right Column */}
            <div className="schedule-section">
              <h3>공사 일정</h3>
              {taskSchedule.length > 0 ? (
                <table className="main-table">
                  <thead>
                    <tr>
                      <th>날짜</th>
                      <th>책임감리</th>
                      <th>보조감리</th>
                      <th>상태</th>
                    </tr>
                  </thead>
                  <tbody>
                    {taskSchedule.map(entry => {
                      const getStatusStyle = (status) => {
                        switch (status) {
                          case '배치계획':
                            return { backgroundColor: 'yellow' };
                          case '배치완료':
                            return { backgroundColor: 'lightgreen' };
                          default:
                            return {};
                        }
                      };

                      const isReplacement = entry.scheduledDate === task.supervisorChangedDate;

                      return (
                        <tr 
                          key={entry.id} 
                          onClick={!isReadOnly ? () => handleScheduleClick(entry) : undefined} // Disable click if read-only
                          style={{ cursor: !isReadOnly ? 'pointer' : 'default', backgroundColor: entry.scheduledDate === task.reportDate ? '#e0ffe0' : 'transparent' }}
                        >
                          <td>{entry.scheduledDate} {entry.scheduledDate === task.reportDate && <span style={{ color: 'green', fontWeight: 'bold' }}>(신고일)</span>}</td>
                          <td>
                            {entry.assignedManagers}
                            {isReplacement && <span style={{ color: 'red' }}> (교체)</span>}
                          </td>
                          <td>{entry.assignedAssistants || '없음'}</td>
                          <td style={getStatusStyle(entry.deploymentStatus)}>{entry.deploymentStatus}</td> 
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <p>등록된 공사 일정이 없습니다.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AssociationReportDetailModal;